// The sales board's arithmetic: which partner gets which colour, and what each
// chart and figure reads, apart from the screen so it can be tested.
//
// Every amount arrives from `GET /api/reports/sales` already in the report
// currency. Nothing here makes money of its own beyond sums and ratios of it.

import type { SalesAmounts, SalesOpenDeal, SalesReport } from "@crm/shared";
import { amountOf, type Measure } from "./salesModel.js";

/** What the big chart shows: each a month-by-month figure split by partner. */
export type BoardMetric = "arr" | "pipeline" | "new" | "bookings" | "forecast";

export const BOARD_METRICS: readonly BoardMetric[] = [
  "arr",
  "pipeline",
  "new",
  "bookings",
  "forecast",
];

/** What a column is split into. */
export type BoardSplit = "partner" | "deal";

export const METRIC_LABEL: Record<BoardMetric, string> = {
  arr: "Signed ARR",
  pipeline: "Pipeline",
  new: "New pipeline",
  bookings: "Bookings",
  forecast: "Revenue forecast",
};

// ---------------------------------------------------------------- partners

/** The id the folded partners or deals share. Not a uuid, so it never meets a real one. */
export const OTHER = "other";

/** Series past this many fold into "Other": a seventh hue would not be told apart. */
export const COLOURED_PARTNERS = 6;

const SLOT_COLOURS = [
  "var(--series-1)",
  "var(--series-2)",
  "var(--series-3)",
  "var(--series-4)",
  "var(--series-5)",
  "var(--series-6)",
];

export interface PartnerSlot {
  /** A partner's or a deal's id, or {@link OTHER}. */
  readonly id: string;
  readonly name: string;
  readonly colour: string;
}

export interface PartnerSlots {
  /** Drawing order, bottom of a stack first; "Other" last when anything folded. */
  readonly slots: readonly PartnerSlot[];
  /** The slot a partner's figures are drawn in. */
  readonly slotOf: (partnerId: string) => PartnerSlot;
  /** A partner's own colour, grey when it is folded. */
  readonly colourOf: (partnerId: string) => string;
}

/**
 * Each partner's place, by how much of the business it carried across the
 * window: its pipeline plus its signed ARR, month by month, plus what it won.
 * One assignment per report, so a partner is the same colour on every tab.
 */
export function partnerSlots(report: SalesReport, measure: Measure): PartnerSlots {
  const size = new Map<string, number>();
  for (const bucket of report.buckets) {
    for (const row of bucket.partners) {
      const add =
        amountOf(row.open, measure) +
        amountOf(row.arr, unweighted(measure)) +
        amountOf(row.won, unweighted(measure));
      size.set(row.partner_id, (size.get(row.partner_id) ?? 0) + add);
    }
  }
  const ranked = report.partners
    .map((partner, index) => ({ partner, index, size: size.get(partner.partner_id) ?? 0 }))
    .filter((row) => row.size > 0 || hasActivity(report, row.partner.partner_id))
    .sort((a, b) => b.size - a.size || a.index - b.index);

  return slotsFor(
    ranked.map((row) => ({ id: row.partner.partner_id, name: row.partner.partner_name })),
    "Other",
  );
}

/** The first few of `ranked` coloured in order, the rest folded into one grey slot. */
function slotsFor(
  ranked: ReadonlyArray<{ id: string; name: string }>,
  otherName: string,
): PartnerSlots {
  const coloured = ranked.slice(0, COLOURED_PARTNERS).map((row, index): PartnerSlot => ({
    id: row.id,
    name: row.name,
    colour: SLOT_COLOURS[index] ?? "var(--series-other)",
  }));
  const folded = ranked.length - coloured.length;
  const other: PartnerSlot = {
    id: OTHER,
    name: folded === 1 ? (ranked[COLOURED_PARTNERS]?.name ?? otherName) : otherName,
    colour: "var(--series-other)",
  };
  const byId = new Map(coloured.map((slot) => [slot.id, slot]));
  return {
    slots: folded > 0 ? [...coloured, other] : coloured,
    slotOf: (id) => byId.get(id) ?? other,
    colourOf: (id) => byId.get(id)?.colour ?? "var(--series-other)",
  };
}

/**
 * The deals of one chart, the biggest over its months first. Each measure has
 * its own deals (a signed customer has no pipeline), so the colours are the
 * chart's own.
 */
export function dealSlots(
  report: SalesReport,
  metric: BoardMetric,
  measure: Measure,
): PartnerSlots {
  const size = new Map<string, number>();
  for (const [dealId, value] of dealValues(report, metric, measure).flat()) {
    size.set(dealId, (size.get(dealId) ?? 0) + value);
  }
  const names = new Map(report.deals.map((deal) => [deal.deal_id, deal.customer_name]));
  const ranked = [...size.entries()]
    .filter(([, value]) => value > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([id]) => ({ id, name: names.get(id) ?? "Deal" }));
  return slotsFor(ranked, "Other deals");
}

/** Each column's values by deal id. */
function dealValues(
  report: SalesReport,
  metric: BoardMetric,
  measure: Measure,
): ReadonlyArray<ReadonlyArray<readonly [string, number]>> {
  if (metric === "forecast") {
    return report.forecast_months.map((month) =>
      month.deals.map((row) => [row.deal_id, amountOf(row.amounts, measure)] as const),
    );
  }
  const m = measureFor(metric, measure);
  return report.buckets.map((bucket) =>
    bucket.deals.map((row) => [row.deal_id, amountOf(pick(row, metric), m)] as const),
  );
}

function hasActivity(report: SalesReport, partnerId: string): boolean {
  return report.buckets.some((bucket) =>
    bucket.partners.some(
      (row) =>
        row.partner_id === partnerId &&
        (row.won_count > 0 || row.new_count > 0 || row.arr_count > 0 || row.open_count > 0),
    ),
  );
}

/** A signed deal or a win is certain: its weighted figure is its full one. */
function unweighted(measure: Measure): Measure {
  return { ...measure, weighted: false };
}

// ------------------------------------------------------------------ columns

export interface ColumnSegment {
  readonly slot: PartnerSlot;
  readonly value: number;
}

export interface Column {
  readonly key: string;
  /** The axis tick: "Jan", or "Jan 26" when the year turns. */
  readonly tick: string;
  /** The full name, for the tooltip and the table: "January 2026". */
  readonly label: string;
  /** In slot order, every slot present (zero when the partner has nothing). */
  readonly segments: readonly ColumnSegment[];
  readonly total: number;
  /**
   * By deal, the column's biggest deals by name for the tooltip, since six
   * colours cannot name a hundred deals; the rest counted.
   */
  readonly detail?: ColumnDetail;
}

export interface ColumnDetail {
  readonly rows: ReadonlyArray<{ id: string; name: string; colour: string; value: number }>;
  readonly moreCount: number;
  readonly moreValue: number;
}

/** How many deals a column's tooltip names. */
const DETAIL_ROWS = 10;

/** The big chart's columns for one metric: the window's months, or the twelve after it. */
export function boardColumns(
  report: SalesReport,
  metric: BoardMetric,
  measure: Measure,
  slots: PartnerSlots,
  split: BoardSplit = "partner",
): readonly Column[] {
  if (split === "deal") {
    const keys =
      metric === "forecast"
        ? report.forecast_months.map((month) => month.month)
        : report.buckets.map((bucket) => bucket.start.slice(0, 7));
    const names = new Map(report.deals.map((deal) => [deal.deal_id, deal.customer_name]));
    return dealValues(report, metric, measure).map((values, i) => {
      const sorted = values.filter(([, value]) => value > 0).sort((a, b) => b[1] - a[1]);
      const rest = sorted.slice(DETAIL_ROWS);
      return {
        ...column(keys[i] ?? "", slots, values),
        detail: {
          rows: sorted.slice(0, DETAIL_ROWS).map(([id, value]) => ({
            id,
            name: names.get(id) ?? "Deal",
            colour: slots.colourOf(id),
            value,
          })),
          moreCount: rest.length,
          moreValue: rest.reduce((sum, [, value]) => sum + value, 0),
        },
      };
    });
  }
  if (metric === "forecast") {
    return report.forecast_months.map((month) =>
      column(
        month.month,
        slots,
        month.partners.map((row) => [row.partner_id, amountOf(row.amounts, measure)] as const),
      ),
    );
  }
  return report.buckets.map((bucket) =>
    column(
      bucket.start.slice(0, 7),
      slots,
      bucket.partners.map(
        (row) =>
          [row.partner_id, amountOf(pick(row, metric), measureFor(metric, measure))] as const,
      ),
    ),
  );
}

function pick(
  row: Pick<SalesReport["buckets"][number]["partners"][number], "arr" | "open" | "new" | "won">,
  metric: Exclude<BoardMetric, "forecast">,
): SalesAmounts {
  switch (metric) {
    case "arr":
      return row.arr;
    case "pipeline":
      return row.open;
    case "new":
      return row.new;
    case "bookings":
      return row.won;
  }
}

/** ARR and bookings are signed money, so the weighting toggle does not apply to them. */
export function measureFor(metric: BoardMetric, measure: Measure): Measure {
  return metric === "arr" || metric === "bookings" ? unweighted(measure) : measure;
}

function column(
  isoMonth: string,
  slots: PartnerSlots,
  values: ReadonlyArray<readonly [string, number]>,
): Column {
  const sums = new Map<string, number>();
  for (const [partnerId, value] of values) {
    const slot = slots.slotOf(partnerId);
    sums.set(slot.id, (sums.get(slot.id) ?? 0) + value);
  }
  const segments = slots.slots.map((slot) => ({ slot, value: sums.get(slot.id) ?? 0 }));
  return {
    key: isoMonth,
    tick: monthTick(isoMonth),
    label: monthLabel(isoMonth),
    segments,
    total: segments.reduce((sum, segment) => sum + segment.value, 0),
  };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Mar", or "Jan 26" for a January, so the axis says where the year turns. */
export function monthTick(isoMonth: string): string {
  const month = Number(isoMonth.slice(5, 7));
  const name = MONTHS[month - 1] ?? isoMonth;
  return month === 1 ? `${name} ${isoMonth.slice(2, 4)}` : name;
}

export function monthLabel(isoMonth: string): string {
  const name = MONTHS[Number(isoMonth.slice(5, 7)) - 1] ?? isoMonth;
  return `${name} ${isoMonth.slice(0, 4)}`;
}

// ----------------------------------------------------------------- figures

export interface BoardFigure {
  readonly value: number;
  /** What it was as the period started, for a level; null for a flow. */
  readonly start: number | null;
  readonly count: number;
}

export interface BoardFigures {
  readonly arr: BoardFigure;
  readonly pipeline: BoardFigure;
  readonly added: BoardFigure;
  readonly bookings: BoardFigure;
}

/** The period's four money figures, from the report that covers exactly the period. */
export function boardFigures(period: SalesReport, measure: Measure): BoardFigures {
  const rows = period.buckets.flatMap((bucket) => bucket.partners);
  const last = period.buckets[period.buckets.length - 1];
  const certain = unweighted(measure);
  return {
    arr: {
      value: amountOf(period.arr_closing, certain),
      start: amountOf(period.arr_opening, certain),
      count: last?.partners.reduce((sum, row) => sum + row.arr_count, 0) ?? 0,
    },
    pipeline: {
      value: amountOf(period.bridge.closing, measure),
      start: amountOf(period.bridge.opening, measure),
      count: last?.open_count ?? 0,
    },
    added: {
      value: rows.reduce((sum, row) => sum + amountOf(row.new, measure), 0),
      start: null,
      count: rows.reduce((sum, row) => sum + row.new_count, 0),
    },
    bookings: {
      value: rows.reduce((sum, row) => sum + amountOf(row.won, certain), 0),
      start: null,
      count: rows.reduce((sum, row) => sum + row.won_count, 0),
    },
  };
}

/** Each month's total of one metric, for a figure's sparkline. */
export function monthlyTotals(
  report: SalesReport,
  metric: Exclude<BoardMetric, "forecast">,
  measure: Measure,
): readonly number[] {
  const m = measureFor(metric, measure);
  return report.buckets.map((bucket) =>
    bucket.partners.reduce((sum, row) => sum + amountOf(pick(row, metric), m), 0),
  );
}

export interface WinRecord {
  readonly won: number;
  readonly lost: number;
  /** Won over won-plus-lost, by count. Null when nothing closed. */
  readonly rate: number | null;
}

export function winRecord(won: number, lost: number): WinRecord {
  const closed = won + lost;
  return { won, lost, rate: closed === 0 ? null : won / closed };
}

// ---------------------------------------------------------------- scorecard

export interface ScorecardRow {
  readonly partnerId: string;
  readonly name: string;
  readonly colour: string;
  /** Pipeline as the window ends. */
  readonly pipeline: number;
  readonly openCount: number;
  /** Signed ARR as the window ends. */
  readonly arr: number;
  /** Won during the window, full value. */
  readonly won: number;
  readonly record: WinRecord;
  readonly cycleDays: number | null;
}

/**
 * One row per partner with anything in the window, biggest first: what it has
 * signed, what it has open, and how its deals have closed.
 */
export function scorecard(
  report: SalesReport,
  measure: Measure,
  slots: PartnerSlots,
): readonly ScorecardRow[] {
  const last = report.buckets[report.buckets.length - 1];
  const arr = new Map(
    (last?.partners ?? []).map((row) => [row.partner_id, amountOf(row.arr, unweighted(measure))]),
  );
  return report.partners
    .map((partner): ScorecardRow => ({
      partnerId: partner.partner_id,
      name: partner.partner_name,
      colour: slots.colourOf(partner.partner_id),
      pipeline: amountOf(partner.open, measure),
      openCount: partner.open_count,
      arr: arr.get(partner.partner_id) ?? 0,
      won: amountOf(partner.won, unweighted(measure)),
      record: winRecord(partner.won_count, partner.lost_count),
      cycleDays: partner.median_cycle_days ?? null,
    }))
    .filter(
      (row) =>
        row.pipeline > 0 || row.arr > 0 || row.won > 0 || row.record.won + row.record.lost > 0,
    )
    .sort((a, b) => b.arr + b.pipeline - (a.arr + a.pipeline) || a.name.localeCompare(b.name));
}

/** The biggest open deals as the period ends, of those `keep` allows. */
export function topDeals(
  period: SalesReport,
  measure: Measure,
  count: number,
  keep: (deal: SalesOpenDeal) => boolean = () => true,
): readonly SalesOpenDeal[] {
  return period.open_deals
    .filter(keep)
    .sort((a, b) => amountOf(b.amounts, measure) - amountOf(a.amounts, measure))
    .slice(0, count);
}

// ------------------------------------------------------------------- window

/**
 * The twelve months the charts cover: the period's last month and the eleven
 * before it, or the whole period when it is longer. A board reads a trend over
 * a year whatever period the figures are for.
 */
export function trendWindow(periodFrom: string, periodTo: string): { from: string; to: string } {
  const year = Number(periodTo.slice(0, 4));
  const month = Number(periodTo.slice(5, 7));
  const startIndex = year * 12 + (month - 1) - 11;
  const start = `${Math.floor(startIndex / 12)}-${String((startIndex % 12) + 1).padStart(2, "0")}-01`;
  return { from: periodFrom < start ? periodFrom.slice(0, 8) + "01" : start, to: periodTo };
}
